import { useEffect, useMemo, useState } from "react";
import {
  Container,
  Title,
  Text,
  Paper,
  Table,
  Group,
  Button,
  Badge,
  Stack,
  Pagination,
  Skeleton,
  Select,
} from "@mantine/core";
import { IconRefresh, IconArchive } from "@tabler/icons-react";
import { Endpoints } from "../../../../lib/endpoints";
import { HttpCodes } from "../../../../lib/data";
import {
  usePerformRequest,
  PerformRequest,
} from "../../../../hooks/usePerformRequest";
import PermissionGuard from "../../../../components/PermissionGuard";
import { AdminPermissions } from "../../../../lib/data";

interface ArchivedMember {
  _id: string;
  firstName?: string;
  lastName?: string;
  email: string;
  balance?: number;
  archivedReason?: string;
  archivedAt?: string;
  isSuspended?: boolean;
  lastAppOpenDate?: string;
  lastLoginAt?: string;
  createdAt?: string;
}

interface ArchiveResponse {
  rows: ArchivedMember[];
  total: number;
  page: number;
  limit: number;
  byReason: { _id: string; n: number }[];
}

const REASON_LABEL: Record<string, string> = {
  bad: "Farm / duplicate / banned",
  inactive: "Inactive 2+ months",
  farm: "Farm",
  duplicate: "Duplicate",
  banned: "Banned",
  suspended: "Suspended",
  manual: "Manual",
};

const Archive = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(25);
  const [reasonFilter, setReasonFilter] = useState<string>("");
  const [restoring, setRestoring] = useState<string | null>(null);

  useEffect(() => {
    document.title = "Archived Members | Virel Admin";
  }, []);

  const endpoint = useMemo(
    () =>
      Endpoints.GetArchivedMembers({
        page: currentPage,
        limit: pageSize,
        reason: reasonFilter || undefined,
      }),
    [currentPage, pageSize, reasonFilter],
  );

  const { data, isLoading, reloadData } = usePerformRequest<{
    data: ArchiveResponse;
    message: string;
  }>({
    method: endpoint.method,
    route: endpoint.route,
    showError: true,
  });

  // The hook exposes the FULL response envelope ({ data, message }); the payload
  // (rows/total/byReason) lives under `.data` — same as every other admin page
  // (see Deletions). Reading data?.rows directly always found nothing → the page
  // showed "0 archived" even though the API returns them.
  const payload = data?.data;
  const rowsData = payload?.rows ?? [];
  const total = payload?.total ?? 0;
  const byReason = payload?.byReason ?? [];
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const formatDate = (value?: string) => {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return (
      d.toLocaleDateString() +
      " " +
      d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    );
  };

  const lastSeen = (m: ArchivedMember) => {
    const dates = [m.lastAppOpenDate, m.lastLoginAt]
      .filter(Boolean)
      .map((x) => new Date(x as string).getTime());
    if (!dates.length) return "Never";
    return formatDate(new Date(Math.max(...dates)).toISOString());
  };

  const reasonBadge = (reason?: string) => {
    const bad = reason === "bad" || reason === "farm" || reason === "duplicate" || reason === "banned" || reason === "suspended";
    return (
      <Badge color={bad ? "red" : "gray"} variant="light">
        {REASON_LABEL[reason || ""] || reason || "—"}
      </Badge>
    );
  };

  const handleRestore = async (m: ArchivedMember) => {
    setRestoring(m._id);
    const ep = Endpoints.RestoreArchivedMember(m._id);
    const result = await PerformRequest({ method: ep.method, route: ep.route });
    setRestoring(null);
    if (result.status === HttpCodes.Success || result.status === HttpCodes.Created) {
      await reloadData();
    }
  };

  const rows = rowsData.map((m) => (
    <Table.Tr key={m._id}>
      <Table.Td>
        <Text fw={500} size="sm">
          {[m.firstName, m.lastName].filter(Boolean).join(" ") || "—"}
        </Text>
        <Text size="xs" c="dimmed">
          {m.email}
        </Text>
      </Table.Td>
      <Table.Td>{reasonBadge(m.archivedReason)}</Table.Td>
      <Table.Td>
        <Text size="sm">₦{(m.balance ?? 0).toLocaleString()}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{lastSeen(m)}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{formatDate(m.archivedAt)}</Text>
      </Table.Td>
      <Table.Td>
        <Button
          size="xs"
          variant="light"
          leftSection={<IconRefresh size={14} />}
          loading={restoring === m._id}
          onClick={() => handleRestore(m)}
        >
          Restore
        </Button>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <PermissionGuard permission={AdminPermissions.MANAGE_USERS}>
      <Container size="xl" py="md">
        <Group justify="space-between" mb="md">
          <div>
            <Group gap="xs">
              <IconArchive size={22} />
              <Title order={3}>Archived Members</Title>
            </Group>
            <Text size="sm" c="dimmed">
              Removed from the live member counts (duplicates, farms, banned,
              2+ months inactive). Their data & balance are kept — restore anyone
              anytime. Inactive members return automatically when they open the app.
            </Text>
          </div>
          <Button
            variant="subtle"
            leftSection={<IconRefresh size={16} />}
            onClick={() => reloadData()}
          >
            Refresh
          </Button>
        </Group>

        <Group mb="md" gap="xs">
          <Badge size="lg" variant="filled" color="dark">
            {total} archived
          </Badge>
          {byReason.map((r) => (
            <Badge key={r._id || "none"} size="lg" variant="light" color={r._id === "inactive" ? "gray" : "red"}>
              {REASON_LABEL[r._id] || r._id || "other"}: {r.n}
            </Badge>
          ))}
        </Group>

        <Group mb="sm">
          <Select
            label="Filter by reason"
            placeholder="All"
            clearable
            value={reasonFilter || null}
            onChange={(v) => {
              setReasonFilter(v || "");
              setCurrentPage(1);
            }}
            data={[
              { value: "bad", label: "Farm / duplicate / banned" },
              { value: "inactive", label: "Inactive 2+ months" },
            ]}
            w={260}
          />
        </Group>

        <Paper withBorder radius="md" p="sm">
          {isLoading ? (
            <Stack>
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} height={38} radius="sm" />
              ))}
            </Stack>
          ) : rowsData.length === 0 ? (
            <Text ta="center" c="dimmed" py="xl">
              No archived members{reasonFilter ? " for this reason" : ""}.
            </Text>
          ) : (
            <Table.ScrollContainer minWidth={720}>
              <Table striped highlightOnHover verticalSpacing="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Member</Table.Th>
                    <Table.Th>Reason</Table.Th>
                    <Table.Th>Balance</Table.Th>
                    <Table.Th>Last seen</Table.Th>
                    <Table.Th>Archived</Table.Th>
                    <Table.Th>Action</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>{rows}</Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </Paper>

        {totalPages > 1 && (
          <Group justify="center" mt="md">
            <Pagination
              value={currentPage}
              onChange={setCurrentPage}
              total={totalPages}
            />
          </Group>
        )}
      </Container>
    </PermissionGuard>
  );
};

export default Archive;
